import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { BusinessAppElementIn, ConfigurationItem, FlowApp, FlowHost, FlowLink, ProgramVulnerabilities, SoftwareFlow as Dati } from '../api/types'
import { useAmministra } from '../components/BusinessApps'
import { dovesiTrova } from '../components/Sbom'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Mappa Software: the application flow around a CI. Columns by level — who
 * calls it on the left, the CI in the middle, what it calls on the right —
 * each host a box with its programs and the ports they listen on, arrows from
 * the calling program to the program answering. Programs and hosts can be
 * excluded; the chosen programs become a Business App (or join one). See
 * software_flow_service and docs/architecture/software-flow.md.
 */

const COL_W = 300
const COL_GAP = 150
const HEAD_H = 46
const ROW_H = 30
const BOX_GAP = 26
const PAD = 24

type Box = { host: FlowHost; x: number; y: number; h: number; rows: Map<string, number> }

function nomeApp(a: FlowApp): string {
  if (a.kind === 'unknown') return t('processo non noto')
  if (a.kind === 'port') return a.name || t('porta {n}', { n: a.ports[0]?.port ?? '' })
  return a.label || a.name || a.process || '—'
}

/** The endpoints shown as chips: ports, then Unix socket files. */
function chipsDi(a: FlowApp): Array<{ k: string; titolo: string }> {
  return [
    ...a.ports.map((p) => ({ k: String(p.port), titolo: `${p.protocol} ${p.port}${p.service ? ` ${p.service}` : ''}` })),
    ...(a.sockets ?? []).map((s) => ({ k: nomeSocket(s).slice(0, 6), titolo: s })),
  ]
}

function nomeSocket(path: string | null | undefined): string {
  return (path ?? '').split('/').pop() || (path ?? '')
}

function etichettaArco(l: FlowLink): string {
  if (l.protocol === 'unix') return nomeSocket(l.path)
  return `${l.port}${l.service ? ` ${l.service}` : ''}`
}

function sottotitoloApp(a: FlowApp): string {
  const parti: string[] = []
  if (a.process && a.process !== a.name) parti.push(a.process)
  if (a.software && a.software !== a.name) parti.push(a.software)
  if (a.kind === 'port') parti.push(t('nessun processo noto in ascolto'))
  else if (a.kind === 'otel') parti.push('OpenTelemetry')
  else if (!['process', 'unknown'].includes(a.kind)) parti.push(a.kind)
  return parti.join(' · ')
}

// Calls are coloured by what they are, so a database call stands out from a
// web one at a glance; the line says how it was seen (dashed: only from
// network flows) and errors win over everything.
type Categoria = 'web' | 'db' | 'messaggi' | 'infra' | 'locale' | 'altro'

const COLORI: Record<Categoria, string> = {
  web: '#2563eb', db: '#9333ea', messaggi: '#0d9488', infra: '#64748b', locale: '#db2777', altro: '#475569',
}
const COLORE_ERRORE = '#dc2626'
const MARKER = [...Object.values(COLORI), COLORE_ERRORE]

const PORTE_DB = new Set([1433, 1521, 3306, 5432, 5984, 6379, 7000, 9042, 11211, 26257, 27017, 50000, 8086, 9200])
const PORTE_MESSAGGI = new Set([1883, 4222, 5671, 5672, 8883, 9092, 61613, 61616])
const PORTE_INFRA = new Set([22, 53, 88, 123, 135, 139, 161, 389, 445, 464, 514, 636, 3268, 3269, 5985, 5986])
const PORTE_WEB = new Set([80, 443, 3000, 5000, 8000, 8008, 8080, 8081, 8443, 8888, 9000, 9443])

function categoria(l: FlowLink): Categoria {
  if (l.protocol === 'unix') return /sql|mysql|pgsql|postgres|redis|mongo/i.test(l.path ?? '') ? 'db' : 'locale'
  if (PORTE_DB.has(l.port)) return 'db'
  if (PORTE_MESSAGGI.has(l.port)) return 'messaggi'
  if (PORTE_INFRA.has(l.port)) return 'infra'
  if (PORTE_WEB.has(l.port) || /http/i.test(l.service ?? '')) return 'web'
  return 'altro'
}

function stileArco(l: FlowLink): { colore: string; tratteggio?: string; spessore: number } {
  const soloFlussi = l.sources.length === 1 && l.sources[0] === 'flow'
  return {
    colore: l.errors > 0 ? COLORE_ERRORE : COLORI[categoria(l)],
    tratteggio: soloFlussi ? '6 4' : undefined,
    // Thicker when the runtime (eBPF, OpenTelemetry) measured it.
    spessore: l.sources.includes('otel') ? 2.4 : 1.6,
  }
}

function disponi(dati: Dati, soloCollegati: boolean): { boxes: Map<string, Box>; w: number; h: number } {
  const usati = new Set(dati.links.flatMap((l) => [l.src, l.dst]))
  const livelli = [...new Set(dati.hosts.map((h) => h.level))].sort((a, b) => a - b)
  const boxes = new Map<string, Box>()
  let altezza = 0
  livelli.forEach((liv, col) => {
    let y = PAD
    for (const host of dati.hosts.filter((h) => h.level === liv)) {
      const apps = host.apps.filter((a) => !soloCollegati || host.focus || usati.has(a.key))
      const rows = new Map<string, number>()
      apps.forEach((a, i) => rows.set(a.key, y + HEAD_H + i * ROW_H + ROW_H / 2))
      const h = HEAD_H + Math.max(1, apps.length) * ROW_H + 8
      boxes.set(host.key, { host: { ...host, apps }, x: PAD + col * (COL_W + COL_GAP), y, h, rows })
      y += h + BOX_GAP
    }
    altezza = Math.max(altezza, y)
  })
  // Room on the right for the arcs of the calls inside a machine.
  const interne = dati.links.some((l) => l.src_host === l.dst_host)
  return {
    boxes,
    w: PAD * 2 + livelli.length * COL_W + Math.max(0, livelli.length - 1) * COL_GAP + (interne ? 150 : 0),
    h: altezza + PAD,
  }
}

function tracciato(a: { x: number; y: number }, b: { x: number; y: number }, stessoHost: boolean): string {
  if (stessoHost) {
    const out = 46 + Math.min(60, Math.abs(b.y - a.y) / 3)
    return `M ${a.x} ${a.y} C ${a.x + out} ${a.y}, ${b.x + out} ${b.y}, ${b.x} ${b.y}`
  }
  const dx = Math.max(60, Math.abs(b.x - a.x) / 2)
  return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`
}

export default function SoftwareFlow() {
  const [params, setParams] = useSearchParams()
  const ciId = params.get('ci')
  const [depth, setDepth] = useState(2)
  // Traffic window in minutes: from the last five minutes to thirty days.
  const [minuti, setMinuti] = useState(24 * 60)
  const [comuni, setComuni] = useState(false)
  const [flussi, setFlussi] = useState(true)
  const [soloCollegati, setSoloCollegati] = useState(false)
  const [esclusi, setEsclusi] = useState<string[]>([])
  const [scelti, setScelti] = useState<Map<string, BusinessAppElementIn>>(new Map())
  const [sopra, setSopra] = useState<string | null>(null)
  const [arco, setArco] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  // The host opened in «Dentro la macchina»: its services and internal calls.
  const [dentro, setDentro] = useState<string | null>(null)
  // The program whose vulnerable libraries are open in the popup.
  const [vulnDi, setVulnDi] = useState<{ host: FlowHost; app: FlowApp } | null>(null)
  // «Segui il percorso»: a call (or a program) chosen, the whole chain it
  // belongs to — who leads to it and where it goes next — lights up.
  const [percorso, setPercorso] = useState(false)
  const [origine, setOrigine] = useState<string | null>(null)
  const [schermoIntero, setSchermoIntero] = useState(false)
  useEffect(() => {
    if (!schermoIntero) return
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setSchermoIntero(false) }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [schermoIntero])

  useEffect(() => { setEsclusi([]); setScelti(new Map()); setArco(null); setDentro(null); setOrigine(null); setVulnDi(null) }, [ciId])

  const dati = useAsync(
    () => (ciId ? api.softwareFlow(ciId, { depth, windowMinutes: minuti, common: comuni, flows: flussi, exclude: esclusi })
      : Promise.resolve(null)),
    [ciId, depth, minuti, comuni, flussi, esclusi.join(',')],
  )
  const layout = useMemo(() => (dati.data ? disponi(dati.data, soloCollegati) : null), [dati.data, soloCollegati])
  const nomiHost = useMemo(() => new Map((dati.data?.hosts ?? []).map((h) => [h.key, h.name])), [dati.data])
  const nomiApp = useMemo(() => new Map((dati.data?.hosts ?? []).flatMap((h) => h.apps.map((a) => [a.key, nomeApp(a)] as const))), [dati.data])

  const escludi = (key: string) => setEsclusi((e) => (e.includes(key) ? e : [...e, key]))
  const commuta = (host: FlowHost, a: FlowApp) => {
    if (!host.ci_id || a.kind === 'unknown') return
    const next = new Map(scelti)
    if (next.has(a.key)) next.delete(a.key)
    else next.set(a.key, {
      ci_id: host.ci_id, software_key: a.key.split('|').slice(1).join('|'), name: nomeApp(a),
      kind: a.kind, process: a.process ?? '', ports: a.ports.map((p) => p.port),
    })
    setScelti(next)
  }

  const arcoScelto = dati.data?.links.find((l) => l.key === arco) ?? null
  const nelPercorso = useMemo(() => {
    if (!percorso || !origine || !dati.data) return null
    const links = dati.data.links
    const out = new Set<string>()
    const partenza = links.find((l) => l.key === origine)
    const giu = partenza ? [partenza.dst] : [origine]
    const su = partenza ? [partenza.src] : [origine]
    if (partenza) out.add(partenza.key)
    const visita = (coda: string[], avanti: boolean) => {
      const visti = new Set(coda)
      while (coda.length) {
        const nodo = coda.shift()!
        for (const l of links) {
          if ((avanti ? l.src : l.dst) !== nodo) continue
          out.add(l.key)
          const prossimo = avanti ? l.dst : l.src
          if (!visti.has(prossimo)) { visti.add(prossimo); coda.push(prossimo) }
        }
      }
    }
    visita(giu, true)
    visita(su, false)
    return out
  }, [percorso, origine, dati.data])
  const evidenziati = useMemo(() => {
    if (!sopra || !dati.data) return null
    return new Set(dati.data.links.filter((l) => l.src === sopra || l.dst === sopra || l.src_host === sopra || l.dst_host === sopra).map((l) => l.key))
  }, [sopra, dati.data])

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Mappa Software')}</h1>
          <p className="page__subtitle">
            {t('Rileva le dipendenze applicative dei CI a partire dai processi in esecuzione, dalle porte in ascolto e dalle comunicazioni, ed evidenzia le relazioni in ingresso e in uscita tra i componenti software. Identifica i componenti rilevanti e li usa per la definizione delle Business App.')}
          </p>
        </div>
      </header>

      <Card>
        <SceltaCI attuale={ciId} nome={dati.data?.hosts.find((h) => h.focus)?.name ?? null}
          onScegli={(id) => setParams({ ci: id })} />
        {ciId && (
          <div className="toolbar" style={{ marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
            <label>{t('Profondità')}{' '}
              <select value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
                {[1, 2, 3, 4].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label>{t('Traffico')}{' '}
              <select value={minuti} onChange={(e) => setMinuti(Number(e.target.value))}>
                <option value={5}>{t('ultimi 5 minuti')}</option>
                <option value={15}>{t('ultimi 15 minuti')}</option>
                <option value={60}>{t('ultima ora')}</option>
                <option value={6 * 60}>{t('ultime 6 ore')}</option>
                <option value={24 * 60}>{t('ultime 24 ore')}</option>
                <option value={7 * 24 * 60}>{t('ultimi 7 giorni')}</option>
                <option value={30 * 24 * 60}>{t('ultimi 30 giorni')}</option>
              </select>
            </label>
            {minuti < 60 && (
              <small className="text-muted" style={{ flexBasis: '100%' }}>
                {t('Finestra breve: i flussi di rete sono quelli degli ultimi minuti, le chiamate misurate dal runtime sono contate per ora (si vede l’ora in corso) e le connessioni sono quelle dell’ultimo inventario.')}
              </small>
            )}
            <label className="checkbox"><input type="checkbox" checked={flussi} onChange={(e) => setFlussi(e.target.checked)} /> {t('Chiamate dai flussi di rete')}</label>
            <label className="checkbox"><input type="checkbox" checked={comuni} onChange={(e) => setComuni(e.target.checked)} /> {t('Servizi comuni (DNS, NTP, monitoraggio…)')}</label>
            <label className="checkbox"><input type="checkbox" checked={soloCollegati} onChange={(e) => setSoloCollegati(e.target.checked)} /> {t('Solo i programmi con chiamate')}</label>
            <span style={{ flex: 1 }} />
            <button className="secondary" onClick={() => setZoom((z) => Math.max(0.4, z - 0.15))} aria-label={t('Riduci')}>−</button>
            <button className="secondary" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
            <button className="secondary" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} aria-label={t('Ingrandisci')}>+</button>
          </div>
        )}
        {esclusi.length > 0 && (
          <p style={{ marginBottom: 0 }}>
            <small className="text-muted">{t('Esclusi')}: </small>
            {esclusi.map((k) => (
              <button key={k} className="secondary" style={{ marginRight: 6 }} title={t('Rimetti sulla mappa')}
                onClick={() => setEsclusi((e) => e.filter((x) => x !== k))}>
                {nomiApp.get(k) ?? nomiHost.get(k) ?? k} ↺
              </button>
            ))}
            <button className="secondary" onClick={() => setEsclusi([])}>{t('Rimetti tutto')}</button>
          </p>
        )}
      </Card>

      {!ciId && <Card><EmptyState title={t('Scegli un CI')} hint={t('Cerca un server per nome o indirizzo: la mappa parte da lì e segue le chiamate dei suoi programmi.')} /></Card>}
      <ErrorBanner error={dati.error} />
      {ciId && dati.loading && !dati.data && <Spinner />}

      {scelti.size > 0 && <BarraScelta scelti={scelti} onPulisci={() => setScelti(new Map())} />}

      {dati.data && layout && (
        <div className={schermoIntero ? 'mappa-sw mappa-sw--intera' : 'mappa-sw'}>
        <Card>
          <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <label className="checkbox" title={t('Scegli una chiamata o un programma: si illumina tutto il giro, da dove parte a dove arriva')}>
              <input type="checkbox" checked={percorso} onChange={(e) => { setPercorso(e.target.checked); setOrigine(null) }} />{' '}
              {t('Segui il percorso')}
            </label>
            {percorso && (
              <small className="text-muted">
                {origine ? t('Clic su un’altra chiamata o su un programma per cambiare; clic sullo stesso per togliere.') : t('Clic su una chiamata o su un programma.')}
              </small>
            )}
            <span style={{ flex: 1 }} />
            <button className="secondary" onClick={() => setSchermoIntero((v) => !v)}>
              {schermoIntero ? t('Esci dallo schermo intero (Esc)') : t('Schermo intero')}
            </button>
          </div>
          <Legenda dati={dati.data} />
          {dati.data.links.length === 0 && (
            <p className="field__hint">
              {t('Nessuna chiamata trovata. Le chiamate per processo arrivano dall’inventario avanzato (SSH o WinRM) e dall’agente CENSO, che leggono le connessioni stabilite; quelle OpenTelemetry dall’agente; le altre dai flussi di rete.')}
            </p>
          )}
          <div className="mappa-sw__tela" style={{ overflow: 'auto', maxHeight: '75vh', border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
            <svg width={layout.w * zoom} height={layout.h * zoom} viewBox={`0 0 ${layout.w} ${layout.h}`}
              role="img" aria-label={t('Mappa Software')} style={{ display: 'block', fontFamily: 'var(--font)' }}>
              <defs>
                {MARKER.map((c, i) => (
                  <marker key={c} id={`freccia-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={c} />
                  </marker>
                ))}
              </defs>
              {dati.data.links.map((l) => {
                const a = layout.boxes.get(l.src_host)
                const b = layout.boxes.get(l.dst_host)
                const ya = a?.rows.get(l.src)
                const yb = b?.rows.get(l.dst)
                if (!a || !b || ya == null || yb == null) return null
                const stesso = a === b
                const pa = { x: a.x + COL_W, y: ya }
                const pb = stesso ? { x: b.x + COL_W, y: yb } : { x: b.x, y: yb }
                const s = stileArco(l)
                const marker = MARKER.indexOf(s.colore)
                const illuminato = nelPercorso?.has(l.key) ?? false
                const spento = nelPercorso ? !illuminato : evidenziati && !evidenziati.has(l.key)
                const cifratura = l.encryption === 'plain' ? '⚠ ' : l.encryption === 'tls' ? '🔒 ' : ''
                // The port label sits by the arrow's end (next to the called
                // program), where labels of calls leaving one box do not pile up.
                const out = 46 + Math.min(60, Math.abs(pb.y - pa.y) / 3)
                const mx = stesso ? pa.x + out * 0.75 + 4 : pb.x - 10
                const my = stesso ? (pa.y + pb.y) / 2 + 4 : pb.y - 6
                return (
                  <g key={l.key} opacity={spento ? 0.12 : 1} style={{ cursor: 'pointer' }} onClick={() => {
                    setArco(l.key === arco ? null : l.key)
                    if (percorso) setOrigine(origine === l.key ? null : l.key)
                  }}>
                    <path d={tracciato(pa, pb, stesso)} fill="none" stroke="transparent" strokeWidth={12} />
                    <path d={tracciato(pa, pb, stesso)} fill="none" stroke={s.colore}
                      strokeWidth={l.key === arco || illuminato ? s.spessore + 1.2 : s.spessore}
                      strokeDasharray={illuminato ? undefined : s.tratteggio} className={illuminato ? 'flusso-animato' : undefined}
                      markerEnd={`url(#freccia-${marker})`} />
                    <text x={mx} y={my} textAnchor={stesso ? 'start' : 'end'} fontSize={11}
                      fill={l.encryption === 'plain' ? '#ea580c' : 'var(--text-muted)'} fontWeight={l.encryption === 'plain' ? 600 : 400}
                      stroke="var(--surface-sunken)" strokeWidth={3} paintOrder="stroke">
                      <title>{l.encryption === 'plain' ? t('In chiaro fra due macchine') : l.encryption === 'tls' ? t('Cifrata (TLS)') : l.encryption === 'local' ? t('Dentro la macchina') : t('Cifratura non nota')}</title>
                      {cifratura}{etichettaArco(l)}
                    </text>
                  </g>
                )
              })}
              {[...layout.boxes.values()].map((box) => (
                <HostBox key={box.host.key} box={box} scelti={scelti} onSopra={setSopra}
                  onScegli={(h, a) => (percorso ? setOrigine(origine === a.key ? null : a.key) : commuta(h, a))} onEscludi={escludi}
                  onFuoco={(id) => setParams({ ci: id })} onDentro={setDentro}
                  onVulnerabilita={(h, a) => setVulnDi({ host: h, app: a })} />
              ))}
            </svg>
          </div>
          {dentro && dati.data.hosts.find((h) => h.key === dentro) && (
            <DentroLaMacchina host={dati.data.hosts.find((h) => h.key === dentro)!} dati={dati.data}
              scelti={scelti} onScegli={commuta} nomiHost={nomiHost} onChiudi={() => setDentro(null)}
              onVulnerabilita={(h, a) => setVulnDi({ host: h, app: a })} />
          )}
          {vulnDi && <VulnerabilitaProgramma host={vulnDi.host} app={vulnDi.app} onChiudi={() => setVulnDi(null)} />}
          {arcoScelto && <DettaglioArco l={arcoScelto} nomiHost={nomiHost} nomiApp={nomiApp} onEscludi={() => { escludi(arcoScelto.src); setArco(null) }} />}
          {(dati.data.hidden.length > 0 || dati.data.notes.length > 0) && (
            <p className="field__hint">
              {dati.data.hidden.length > 0 && <>{t('Nascosti')}: {dati.data.hidden.map((h) => `${h.category} (${h.count})`).join(', ')}. </>}
              {dati.data.notes.join('; ')}
            </p>
          )}
        </Card>
        </div>
      )}
    </>
  )
}

function HostBox({ box, scelti, onSopra, onScegli, onEscludi, onFuoco, onDentro, onVulnerabilita }: {
  box: Box
  scelti: Map<string, BusinessAppElementIn>
  onSopra: (k: string | null) => void
  onScegli: (h: FlowHost, a: FlowApp) => void
  onEscludi: (k: string) => void
  onFuoco: (ciId: string) => void
  onDentro: (hostKey: string) => void
  onVulnerabilita: (h: FlowHost, a: FlowApp) => void
}) {
  const { host, x, y, h } = box
  const fonti = [host.data.sbom && 'SBOM', host.data.listening && t('porte'), host.data.connections && t('connessioni')].filter(Boolean).join(' · ')
  return (
    <g onMouseEnter={() => onSopra(host.key)} onMouseLeave={() => onSopra(null)}>
      <rect x={x} y={y} width={COL_W} height={h} rx={8} fill="var(--surface)"
        stroke={host.focus ? 'var(--accent)' : 'var(--border)'} strokeWidth={host.focus ? 2 : 1} />
      <g style={{ cursor: host.ci_id && !host.focus ? 'pointer' : 'default' }}
        onClick={() => host.ci_id && !host.focus && onFuoco(host.ci_id)}>
        <title>{host.ci_id && !host.focus ? t('Metti questo CI al centro della mappa') : host.name}</title>
        <text x={x + 12} y={y + 20} fontSize={13} fontWeight={600} fill="var(--text-heading)">
          {host.name.length > 30 ? `${host.name.slice(0, 29)}…` : host.name}{host.more ? ' ⁺' : ''}
        </text>
        <text x={x + 12} y={y + 36} fontSize={11} fill="var(--text-muted)">
          {[host.ci_class, host.address !== host.name ? host.address : null].filter(Boolean).join(' · ') || (host.ci_id ? '' : t('indirizzo non in CMDB'))}
          {fonti ? ` · ${fonti}` : ''}
        </text>
      </g>
      {host.ci_id && (
        <text x={x + COL_W - (host.focus ? 12 : 30)} y={y + 20} fontSize={11} textAnchor="end" fill="var(--accent)"
          style={{ cursor: 'pointer' }} onClick={() => onDentro(host.key)}>
          <title>{t('Dentro la macchina: i servizi e le chiamate interne')}</title>{t('dentro')} ⤢
        </text>
      )}
      {!host.focus && (
        <text x={x + COL_W - 12} y={y + 20} fontSize={14} textAnchor="end" fill="var(--text-muted)" style={{ cursor: 'pointer' }}
          onClick={() => onEscludi(host.key)}>
          <title>{t('Escludi dalla mappa')}</title>×
        </text>
      )}
      <line x1={x} x2={x + COL_W} y1={y + HEAD_H - 4} y2={y + HEAD_H - 4} stroke="var(--divider)" />
      {host.apps.length === 0 && (
        <text x={x + 12} y={y + HEAD_H + 18} fontSize={11} fill="var(--text-muted)">{t('nessun programma noto')}</text>
      )}
      {host.apps.map((a) => {
        const cy = box.rows.get(a.key) ?? 0
        const scelto = scelti.has(a.key)
        const sceglibile = Boolean(host.ci_id) && a.kind !== 'unknown'
        const tutte = chipsDi(a)
        const porte = tutte.slice(0, 3)
        // Names and subtitles stop before the port chips.
        const vuln = vulnerabilitaDi(a)
        const piu = tutte.length > 3 ? 18 : 0
        const libero = COL_W - 24 - porte.length * 44 - piu - 16 - (vuln ? 44 : 0)
        const tronca = (testo: string, px: number) => {
          const max = Math.max(6, Math.floor(libero / px))
          return testo.length > max ? `${testo.slice(0, max - 1)}…` : testo
        }
        return (
          <g key={a.key} onMouseEnter={(e) => { e.stopPropagation(); onSopra(a.key) }} onMouseLeave={() => onSopra(host.key)}>
            <rect x={x + 4} y={cy - ROW_H / 2 + 2} width={COL_W - 8} height={ROW_H - 4} rx={5}
              fill={scelto ? 'var(--info-bg)' : 'transparent'} stroke={scelto ? 'var(--accent)' : 'none'}
              style={{ cursor: sceglibile ? 'pointer' : 'default' }} onClick={() => onScegli(host, a)}>
              <title>{sceglibile ? t('Scegli per una Business App') : nomeApp(a)}</title>
            </rect>
            <text x={x + 12} y={cy - 1} fontSize={12} fontStyle={a.kind === 'unknown' ? 'italic' : undefined}
              fontWeight={a.sources.includes('sbom') ? 600 : 400} fill="var(--text)" pointerEvents="none">
              {tronca(nomeApp(a), 7)}
            </text>
            <text x={x + 12} y={cy + 11} fontSize={10} fill="var(--text-muted)" pointerEvents="none">
              {tronca(sottotitoloApp(a), 5.6)}
            </text>
            {porte.map((p, i) => (
              <g key={p.k + i}>
                <title>{p.titolo}</title>
                <rect x={x + COL_W - 30 - (porte.length - 1 - i) * 44 - 32} y={cy - 9} width={40} height={18} rx={4}
                  fill="var(--surface-alt)" stroke="var(--border)" />
                <text x={x + COL_W - 30 - (porte.length - 1 - i) * 44 - 12} y={cy + 4} fontSize={10} textAnchor="middle"
                  fontFamily="var(--font-mono)" fill="var(--text)">{p.k}</text>
              </g>
            ))}
            {tutte.length > 3 && (
              <text x={x + COL_W - 30 - porte.length * 44 - 6} y={cy + 4} fontSize={10} textAnchor="end" fill="var(--text-muted)">
                <title>{tutte.map((p) => p.titolo).join(', ')}</title>+{tutte.length - 3}
              </text>
            )}
            {vuln && host.ci_id && (
              <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onVulnerabilita(host, a) }}>
                <title>{vuln.titolo}</title>
                <rect x={x + COL_W - 30 - porte.length * 44 - piu - 40} y={cy - 9} width={36} height={18} rx={9} fill={vuln.colore} />
                <text x={x + COL_W - 30 - porte.length * 44 - piu - 22} y={cy + 4} fontSize={10} fontWeight={700} textAnchor="middle"
                  fill="#fff" pointerEvents="none">⚠ {vuln.n}</text>
              </g>
            )}
            <text x={x + COL_W - 10} y={cy + 4} fontSize={12} textAnchor="end" fill="var(--text-muted)" style={{ cursor: 'pointer' }}
              onClick={() => onEscludi(a.key)}>
              <title>{t('Escludi dalla mappa')}</title>×
            </text>
          </g>
        )
      })}
    </g>
  )
}

function Legenda({ dati }: { dati: Dati }) {
  const n = (x: number) => x.toLocaleString(localeCorrente())
  const programmi = dati.hosts.reduce((s, h) => s + h.apps.filter((a) => a.kind !== 'unknown').length, 0)
  const voce = (colore: string, testo: string, tratteggio?: string) => (
    <span style={{ marginRight: 16, whiteSpace: 'nowrap' }}>
      <svg width={28} height={10} aria-hidden="true"><line x1={0} x2={28} y1={5} y2={5} stroke={colore} strokeWidth={2} strokeDasharray={tratteggio} /></svg>{' '}
      <small>{testo}</small>
    </span>
  )
  return (
    <p style={{ marginTop: 0 }}>
      <small className="text-muted" style={{ marginRight: 16 }}>
        {t('{h} server, {p} programmi, {c} chiamate', { h: n(dati.hosts.length), p: n(programmi), c: n(dati.links.length) })}
      </small>
      {voce(COLORI.web, t('web (HTTP/HTTPS)'))}
      {voce(COLORI.db, t('database'))}
      {voce(COLORI.messaggi, t('code e messaggi'))}
      {voce(COLORI.infra, t('servizi d’infrastruttura'))}
      {voce(COLORI.locale, t('socket locali'))}
      {voce(COLORI.altro, t('altro'))}
      {voce('#64748b', t('solo dai flussi di rete'), '6 4')}
      {voce(COLORE_ERRORE, t('con errori'))}
      <span style={{ marginRight: 16, whiteSpace: 'nowrap' }}><small><span style={{ color: '#ea580c', fontWeight: 600 }}>⚠</span> {t('in chiaro fra macchine')} · 🔒 {t('cifrata')} · {t('linea spessa: misurata dal runtime (eBPF, OpenTelemetry)')}</small></span>
      <small className="text-muted">{t('Clic su un programma per sceglierlo, sul nome di un server per metterlo al centro; ⁺ indica altre chiamate oltre la profondità.')}</small>
    </p>
  )
}

function DettaglioArco({ l, nomiHost, nomiApp, onEscludi }: {
  l: FlowLink; nomiHost: Map<string, string>; nomiApp: Map<string, string>; onEscludi: () => void
}) {
  const fonti: Record<string, string> = {
    connection: t('connessioni stabilite (per processo)'), otel: t('OpenTelemetry'), flow: t('flussi di rete'),
  }
  const n = (x: number) => x.toLocaleString(localeCorrente())
  return (
    <div className="card__inset" style={{ marginTop: 12 }}>
      <strong>{nomiApp.get(l.src)}</strong> <small className="text-muted">({nomiHost.get(l.src_host)})</small>
      {' → '}<strong>{nomiApp.get(l.dst)}</strong> <small className="text-muted">({nomiHost.get(l.dst_host)})</small>
      {' · '}<span className="mono">{l.protocol === 'unix' ? l.path : l.port}</span>{l.protocol !== 'unix' && l.service && ` ${l.service}`}
      <div><small>
        {t('Visto da')}: {l.sources.map((s) => fonti[s] ?? s).join(', ')}
        {l.count > 0 && <> · {t('{n} connessioni o chiamate', { n: n(l.count) })}</>}
        {l.errors > 0 && <> · <span style={{ color: 'var(--error)' }}>{t('{n} errori', { n: n(l.errors) })}</span></>}
        {l.p95_ms > 0 && <> · p95 {l.p95_ms} ms</>}
      </small></div>
      <button className="secondary" style={{ marginTop: 8 }} onClick={onEscludi}>{t('Escludi il programma che chiama')}</button>
    </div>
  )
}

function SceltaCI({ attuale, nome, onScegli }: { attuale: string | null; nome: string | null; onScegli: (id: string) => void }) {
  const [q, setQ] = useState('')
  const [risultati, setRisultati] = useState<ConfigurationItem[]>([])
  useEffect(() => {
    const testo = q.trim()
    if (testo.length < 2) { setRisultati([]); return }
    const timer = setTimeout(() => {
      api.cis({ q: testo, limit: 10 }).then(setRisultati).catch(() => setRisultati([]))
    }, 250)
    return () => clearTimeout(timer)
  }, [q])
  return (
    <div style={{ position: 'relative', maxWidth: 520, display: 'grid', gap: 6 }}>
      <label className="field__label" htmlFor="mappa-sw-ci">
        {attuale && nome ? <>{t('CI al centro')}: <strong>{nome}</strong> · <Link to={`/cis/${attuale}`}>{t('apri la scheda')}</Link></> : t('CI da cui partire')}
      </label>
      <input id="mappa-sw-ci" type="search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={t('Cerca un server per nome o indirizzo')} autoComplete="off" style={{ width: '100%' }} />
      {risultati.length > 0 && (
        <ul className="card" style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, margin: 0, padding: 4, listStyle: 'none' }}>
          {risultati.map((c) => (
            <li key={c.id}>
              <button className="link" style={{ width: '100%', textAlign: 'left', padding: '6px 8px' }}
                onClick={() => { onScegli(c.id); setQ(''); setRisultati([]) }}>
                {c.name ?? c.id} <small className="text-muted">{[c.ci_class, (c.attributes as Record<string, unknown> | undefined)?.mgmt_ip as string | undefined].filter(Boolean).join(' · ')}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function BarraScelta({ scelti, onPulisci }: { scelti: Map<string, BusinessAppElementIn>; onPulisci: () => void }) {
  const admin = useAmministra()
  const apps = useAsync(() => api.businessApps(), [])
  const [nome, setNome] = useState('')
  const [esistente, setEsistente] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState<{ id: string; name: string } | null>(null)
  const elementi = [...scelti.values()]

  async function crea() {
    setErrore(null)
    try {
      const app = await api.createBusinessApp({ name: nome.trim(), elements: elementi })
      setFatto({ id: app.id, name: app.name })
      onPulisci()
    } catch (e) { setErrore(e) }
  }
  async function aggiungi() {
    setErrore(null)
    try {
      const app = await api.addBusinessAppElements(esistente, elementi)
      setFatto({ id: app.id, name: app.name })
      onPulisci()
    } catch (e) { setErrore(e) }
  }

  return (
    <Card title={t('{n} programmi scelti', { n: scelti.size })}>
      <p className="field__hint" style={{ marginTop: 0 }}>{elementi.map((e) => e.name).join(', ')}</p>
      <ErrorBanner error={errore} />
      {fatto && <p>{t('Fatto')}: <Link to={`/business-apps/${fatto.id}`}>{fatto.name}</Link></p>}
      {admin ? (
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('Nome della nuova Business App')} />
          <button disabled={!nome.trim()} onClick={crea}>{t('Crea Business App')}</button>
          {(apps.data ?? []).length > 0 && (
            <>
              <span className="text-muted">{t('oppure')}</span>
              <select value={esistente} onChange={(e) => setEsistente(e.target.value)}>
                <option value="">{t('Aggiungi a una Business App…')}</option>
                {(apps.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button disabled={!esistente} onClick={aggiungi}>{t('Aggiungi')}</button>
            </>
          )}
          <button className="secondary" onClick={onPulisci}>{t('Annulla la scelta')}</button>
        </div>
      ) : (
        <p className="text-muted">{t('Creare una Business App richiede il ruolo di amministratore del tenant.')}</p>
      )}
    </Card>
  )
}

/**
 * Dentro la macchina: one host opened in its own window. Columns from left to
 * right — who calls in from outside, the host's services in layers (from
 * what is reached from outside to what is only called inside), where calls
 * go out — with the order inside each column chosen to cross as few arrows
 * as possible. Hovering a service lights its calls only; clicking it opens
 * its details (ports, sockets, software, calls in and out) and lets it be
 * picked for a Business App. Two services of one program (two JVMs, two
 * php-fpm pools) are two boxes, each with its own calls.
 */
type NodoDentro = { key: string; nome: string; sotto: string; col: number; tipo: 'fuori-in' | 'servizio' | 'fuori-out'; app?: FlowApp }

function DentroLaMacchina({ host, dati, scelti, onScegli, nomiHost, onChiudi, onVulnerabilita }: {
  host: FlowHost
  dati: Dati
  scelti: Map<string, BusinessAppElementIn>
  onScegli: (h: FlowHost, a: FlowApp) => void
  nomiHost: Map<string, string>
  onChiudi: () => void
  onVulnerabilita: (h: FlowHost, a: FlowApp) => void
}) {
  const [zoom, setZoom] = useState(1)
  const [sopra, setSopra] = useState<string | null>(null)
  const [scelto, setScelto] = useState<string | null>(null)
  const tela = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])

  const interni = dati.links.filter((l) => l.src_host === host.key && l.dst_host === host.key)
  const entrano = dati.links.filter((l) => l.dst_host === host.key && l.src_host !== host.key)
  const escono = dati.links.filter((l) => l.src_host === host.key && l.dst_host !== host.key)
  const apps = host.apps.filter((a) => a.kind !== 'unknown')

  // Layers of the services: reached from outside (or not called inside)
  // first, then the longest internal path, bounded.
  const livello = new Map<string, number>()
  const daFuori = new Set(entrano.map((l) => l.dst))
  const chiamatiDentro = new Set(interni.map((l) => l.dst))
  for (const a of apps) if (daFuori.has(a.key) || !chiamatiDentro.has(a.key)) livello.set(a.key, 0)
  for (let giro = 0; giro < apps.length; giro++) {
    let cambiato = false
    for (const l of interni) {
      const da = livello.get(l.src)
      if (da == null || daFuori.has(l.dst)) continue
      const nuovo = Math.min(da + 1, 7)
      if ((livello.get(l.dst) ?? -1) < nuovo) { livello.set(l.dst, nuovo); cambiato = true }
    }
    if (!cambiato) break
  }
  const livelliServizi = Math.max(1, ...[...livello.values()].map((v) => v + 1))
  const conFuoriIn = entrano.length > 0
  const offset = conFuoriIn ? 1 : 0

  // Nodes: external callers (one per host), services, external targets (one per host).
  const nodi: NodoDentro[] = []
  const chiaveFuori = (k: string) => `fuori:${k}`
  for (const k of new Set(entrano.map((l) => l.src_host))) {
    nodi.push({ key: chiaveFuori(k), nome: nomiHost.get(k) ?? k, sotto: t('chiama da fuori'), col: 0, tipo: 'fuori-in' })
  }
  for (const a of apps) {
    const chips = chipsDi(a).map((c) => c.k).join(' · ')
    nodi.push({ key: a.key, nome: nomeApp(a), sotto: [sottotitoloApp(a), chips].filter(Boolean).join(' — '),
      col: offset + (livello.get(a.key) ?? 0), tipo: 'servizio', app: a })
  }
  const colFuoriOut = offset + livelliServizi
  for (const k of new Set(escono.map((l) => l.dst_host))) {
    nodi.push({ key: `verso:${k}`, nome: nomiHost.get(k) ?? k, sotto: t('fuori dalla macchina'), col: colFuoriOut, tipo: 'fuori-out' })
  }
  // Edges in node terms.
  type ArcoDentro = { key: string; da: string; a: string; l: FlowLink; esterno: boolean }
  const archi: ArcoDentro[] = [
    ...entrano.map((l) => ({ key: l.key, da: chiaveFuori(l.src_host), a: l.dst, l, esterno: true })),
    ...interni.map((l) => ({ key: l.key, da: l.src, a: l.dst, l, esterno: false })),
    ...escono.map((l) => ({ key: l.key, da: l.src, a: `verso:${l.dst_host}`, l, esterno: true })),
  ].filter((x) => nodi.some((n) => n.key === x.da) && nodi.some((n) => n.key === x.a))

  // Order inside each column by the average position of the neighbours
  // (two sweeps of the barycentre heuristic): fewer crossings.
  const colonne = Math.max(...nodi.map((n) => n.col)) + 1
  const perCol: string[][] = Array.from({ length: colonne }, () => [])
  for (const n of nodi) perCol[n.col].push(n.key)
  const indice = () => {
    const m = new Map<string, number>()
    perCol.forEach((c) => c.forEach((k, i) => m.set(k, i)))
    return m
  }
  for (let passo = 0; passo < 4; passo++) {
    const pos = indice()
    const ordine = passo % 2 === 0 ? perCol.map((_, i) => i) : perCol.map((_, i) => colonne - 1 - i)
    for (const c of ordine) {
      const peso = (k: string) => {
        const vicini = archi.filter((x) => x.da === k || x.a === k).map((x) => pos.get(x.da === k ? x.a : x.da) ?? 0)
        return vicini.length ? vicini.reduce((s, v) => s + v, 0) / vicini.length : pos.get(k) ?? 0
      }
      perCol[c].sort((a, b) => peso(a) - peso(b))
    }
  }

  const W = 210, GAP = 100, RH = 58, VG = 22
  const pos = new Map<string, { x: number; y: number }>()
  const altezzaCol = (c: number) => perCol[c].length * (RH + VG)
  const alto = Math.max(1, ...perCol.map((_, c) => altezzaCol(c)))
  perCol.forEach((chiavi, c) => {
    const y0 = 30 + (alto - altezzaCol(c)) / 2
    chiavi.forEach((k, i) => pos.set(k, { x: 20 + c * (W + GAP), y: y0 + i * (RH + VG) }))
  })
  const larghezza = 40 + colonne * W + (colonne - 1) * GAP
  const altezza = alto + 60
  // Spread the ends of the arrows along each box's side.
  const lato = (chiave: 'da' | 'a') => {
    const gruppi = new Map<string, ArcoDentro[]>()
    for (const x of archi) gruppi.set(x[chiave], [...(gruppi.get(x[chiave]) ?? []), x])
    const y = new Map<string, number>()
    for (const [k, xs] of gruppi) {
      const altro = chiave === 'da' ? 'a' : 'da'
      xs.sort((p, q) => (pos.get(p[altro])?.y ?? 0) - (pos.get(q[altro])?.y ?? 0))
      const base = pos.get(k)?.y ?? 0
      xs.forEach((x, i) => y.set(x.key, base + (xs.length === 1 ? RH / 2 : 10 + (i * (RH - 20)) / (xs.length - 1))))
    }
    return y
  }
  const yDa = lato('da')
  const yA = lato('a')
  const fuoco = sopra ?? scelto
  const accesi = fuoco ? new Set(archi.filter((x) => x.da === fuoco || x.a === fuoco).map((x) => x.key)) : null
  const nodoScelto = nodi.find((n) => n.key === scelto)
  // Fits the drawing to the window: everything visible at once, zoom to read.
  const adatta = () => {
    const box = tela.current
    if (!box) return
    const z = Math.min((box.clientWidth - 8) / larghezza, (box.clientHeight - 8) / altezza, 1.2)
    // Not below 70%: past that the text is unreadable, and scrolling is better.
    setZoom(Math.max(0.7, Math.round(z * 100) / 100))
  }
  useEffect(() => { adatta() }, [larghezza, altezza, Boolean(nodoScelto)]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Dentro la macchina')}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(1400px, 96vw)', maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Dentro la macchina')}: {host.name}</h3>
            <p className="dob-modal__sotto">
              {t('I servizi della macchina e le chiamate fra loro (loopback, indirizzi della macchina, socket Unix), con chi chiama da fuori a sinistra e dove si esce a destra. Passa sopra un servizio per vederne le chiamate; cliccalo per i dettagli.')}
            </p>
          </div>
          <div className="toolbar" style={{ gap: 6 }}>
            <button className="secondary" onClick={() => setZoom((z) => Math.max(0.4, z - 0.15))} aria-label={t('Riduci')}>−</button>
            <button className="secondary" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
            <button className="secondary" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} aria-label={t('Ingrandisci')}>+</button>
            <button className="secondary" onClick={adatta}>{t('Adatta')}</button>
            <button className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        {interni.length === 0 && (
          <p className="text-muted">{t('Nessuna chiamata interna rilevata: servono l’inventario avanzato aggiornato o l’agente CENSO, che leggono le connessioni in loopback e i socket Unix.')}</p>
        )}
        <div style={{ display: 'flex', gap: 12, minHeight: 0, flex: 1 }}>
          <div ref={tela} style={{ overflow: 'auto', flex: 1, minHeight: '60vh', border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
            <svg width={larghezza * zoom} height={altezza * zoom} viewBox={`0 0 ${larghezza} ${altezza}`}
              role="img" aria-label={t('Dentro la macchina')} style={{ display: 'block', fontFamily: 'var(--font)' }}>
              <defs>
                {MARKER.map((c, i) => (
                  <marker key={c} id={`dentro-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill={c} />
                  </marker>
                ))}
              </defs>
              {conFuoriIn && <text x={20 + W / 2} y={18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{t('da fuori')}</text>}
              <text x={20 + offset * (W + GAP) + (livelliServizi * (W + GAP) - GAP) / 2} y={18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{host.name}</text>
              {escono.length > 0 && <text x={20 + colFuoriOut * (W + GAP) + W / 2} y={18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{t('verso fuori')}</text>}
              {archi.map((x) => {
                const a = pos.get(x.da)
                const b = pos.get(x.a)
                if (!a || !b) return null
                const pa = { x: a.x + W, y: yDa.get(x.key) ?? a.y + RH / 2 }
                const ya = yA.get(x.key) ?? b.y + RH / 2
                const avanti = b.x > a.x
                const pb = avanti ? { x: b.x, y: ya } : { x: b.x + W, y: ya }
                const dx = Math.max(50, Math.abs(pb.x - pa.x) / 2)
                const d = avanti
                  ? `M ${pa.x} ${pa.y} C ${pa.x + dx} ${pa.y}, ${pb.x - dx} ${pb.y}, ${pb.x} ${pb.y}`
                  : `M ${pa.x} ${pa.y} C ${pa.x + 70} ${pa.y}, ${pb.x + 70} ${pb.y}, ${pb.x} ${pb.y}`
                const st = stileArco(x.l)
                const spento = accesi && !accesi.has(x.key)
                return (
                  <g key={x.key} opacity={spento ? 0.1 : x.esterno && !accesi ? 0.55 : 1}>
                    <path d={d} fill="none" stroke={st.colore} strokeWidth={accesi?.has(x.key) ? st.spessore + 1 : st.spessore}
                      strokeDasharray={x.esterno ? '5 4' : st.tratteggio} markerEnd={`url(#dentro-${MARKER.indexOf(st.colore)})`} />
                    <text x={pb.x + (avanti ? -8 : 8)} y={pb.y - 6} textAnchor={avanti ? 'end' : 'start'} fontSize={11}
                      fill={x.l.encryption === 'plain' ? '#ea580c' : 'var(--text-muted)'} stroke="var(--surface-sunken)" strokeWidth={3} paintOrder="stroke">
                      {x.l.encryption === 'plain' ? '⚠ ' : x.l.encryption === 'tls' ? '🔒 ' : ''}{etichettaArco(x.l)}
                    </text>
                  </g>
                )
              })}
              {nodi.map((n) => {
                const p = pos.get(n.key)
                if (!p) return null
                const servizio = n.tipo === 'servizio'
                const sel = scelto === n.key || (n.app && scelti.has(n.app.key))
                return (
                  <g key={n.key} style={{ cursor: 'pointer' }} onMouseEnter={() => setSopra(n.key)} onMouseLeave={() => setSopra(null)}
                    onClick={() => setScelto(scelto === n.key ? null : n.key)}>
                    <rect x={p.x} y={p.y} width={W} height={RH} rx={8}
                      fill={servizio ? (sel ? 'var(--info-bg)' : 'var(--surface)') : 'var(--surface-alt)'}
                      stroke={sel ? 'var(--accent)' : 'var(--border)'} strokeWidth={sel ? 2 : 1}
                      strokeDasharray={servizio ? undefined : '4 3'} />
                    <text x={p.x + 10} y={p.y + 20} fontSize={12.5} fontWeight={600} fill="var(--text)">{n.nome.length > 30 ? `${n.nome.slice(0, 29)}…` : n.nome}</text>
                    <text x={p.x + 10} y={p.y + 36} fontSize={10.5} fill="var(--text-muted)">{n.sotto.length > 40 ? `${n.sotto.slice(0, 39)}…` : n.sotto}</text>
                    {servizio && n.app && (
                      <text x={p.x + 10} y={p.y + 51} fontSize={10} fill="var(--text-muted)">
                        {t('{i} in entrata · {u} in uscita', {
                          i: archi.filter((x) => x.a === n.key).length, u: archi.filter((x) => x.da === n.key).length,
                        })}
                      </text>
                    )}
                  </g>
                )
              })}
            </svg>
          </div>
          {nodoScelto && (
            <DettaglioServizio nodo={nodoScelto} archi={archi} host={host} nomiHost={nomiHost}
              nomeDi={(k) => nodi.find((n) => n.key === k)?.nome ?? k}
              scelto={Boolean(nodoScelto.app && scelti.has(nodoScelto.app.key))}
              onScegli={() => nodoScelto.app && onScegli(host, nodoScelto.app)} onChiudi={() => setScelto(null)}
              onVulnerabilita={() => nodoScelto.app && onVulnerabilita(host, nodoScelto.app)} />
          )}
        </div>
      </div>
    </div>
  )
}

function DettaglioServizio({ nodo, archi, host, nomeDi, scelto, onScegli, onChiudi, onVulnerabilita }: {
  nodo: NodoDentro
  archi: Array<{ key: string; da: string; a: string; l: FlowLink }>
  host: FlowHost
  nomiHost: Map<string, string>
  nomeDi: (k: string) => string
  scelto: boolean
  onScegli: () => void
  onChiudi: () => void
  onVulnerabilita: () => void
}) {
  const a = nodo.app
  const vuln = a ? vulnerabilitaDi(a) : null
  const entrata = archi.filter((x) => x.a === nodo.key)
  const uscita = archi.filter((x) => x.da === nodo.key)
  const riga = (x: { l: FlowLink }, altro: string) => (
    <li key={x.l.key}>
      <small>
        {altro} · <span className="mono">{etichettaArco(x.l)}</span>
        {x.l.encryption === 'plain' && <span style={{ color: '#ea580c' }}> ⚠ {t('in chiaro')}</span>}
        {x.l.count > 0 && <span className="text-muted"> · {x.l.count}</span>}
        {x.l.errors > 0 && <span style={{ color: COLORE_ERRORE }}> · {t('{n} errori', { n: x.l.errors })}</span>}
        {x.l.p95_ms > 0 && <span className="text-muted"> · p95 {x.l.p95_ms} ms</span>}
      </small>
    </li>
  )
  return (
    <aside style={{ width: 320, flexShrink: 0, overflow: 'auto', border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)', padding: 12, background: 'var(--surface)' }}>
      <div className="toolbar" style={{ marginBottom: 6 }}>
        <strong style={{ flex: 1 }}>{nodo.nome}</strong>
        <button className="link" onClick={onChiudi} aria-label={t('Chiudi')}>×</button>
      </div>
      {a ? (
        <>
          {a.process && <p style={{ margin: '4px 0' }}><small className="text-muted">{t('Processo')}:</small> <span className="mono">{a.process}</span></p>}
          {a.software && <p style={{ margin: '4px 0' }}><small className="text-muted">{t('Software')}:</small> {a.software}</p>}
          {a.path && <p style={{ margin: '4px 0' }}><small className="text-muted">{t('Percorso')}:</small> <span className="mono" style={{ wordBreak: 'break-all' }}>{a.path}</span></p>}
          {chipsDi(a).length > 0 && <p style={{ margin: '4px 0' }}><small className="text-muted">{t('In ascolto')}:</small> <span className="mono">{chipsDi(a).map((c) => c.titolo).join(', ')}</span></p>}
          {vuln && host.ci_id && (
            <p style={{ margin: '6px 0' }}>
              <button className="link" style={{ color: vuln.colore, fontWeight: 600 }} onClick={onVulnerabilita}>⚠ {vuln.titolo}</button>
            </p>
          )}
          {host.ci_id && (
            <button className={scelto ? 'secondary' : ''} style={{ margin: '8px 0' }} onClick={onScegli}>
              {scelto ? t('Togli dalla scelta') : t('Scegli per una Business App')}
            </button>
          )}
        </>
      ) : (
        <p className="text-muted"><small>{nodo.sotto}</small></p>
      )}
      <h4 style={{ margin: '10px 0 4px' }}>{t('In entrata ({n})', { n: entrata.length })}</h4>
      {entrata.length === 0 ? <small className="text-muted">—</small> : <ul style={{ paddingLeft: 16, margin: 0 }}>{entrata.map((x) => riga(x, nomeDi(x.da)))}</ul>}
      <h4 style={{ margin: '10px 0 4px' }}>{t('In uscita ({n})', { n: uscita.length })}</h4>
      {uscita.length === 0 ? <small className="text-muted">—</small> : <ul style={{ paddingLeft: 16, margin: 0 }}>{uscita.map((x) => riga(x, nomeDi(x.a)))}</ul>}
    </aside>
  )
}

/** The badge of a program with vulnerable libraries or package CVEs: red
 *  when one is exploited in the wild or critical, orange otherwise. */
function vulnerabilitaDi(a: FlowApp): { n: number; colore: string; titolo: string } | null {
  const v = a.vulnerabilities
  if (!v || v.libraries + v.package === 0) return null
  const parti = []
  if (v.libraries) parti.push(t('{n} librerie vulnerabili', { n: v.libraries }))
  if (v.package) parti.push(t('{n} CVE del pacchetto', { n: v.package }))
  if (v.exploited) parti.push(t('sfruttata attivamente'))
  return {
    n: v.libraries + v.package,
    colore: v.exploited || (v.cvss ?? 0) >= 9 ? COLORE_ERRORE : '#ea580c',
    titolo: `${parti.join(' · ')} — ${t('clic per il dettaglio')}`,
  }
}

/** Score and «exploited» of an advisory, as one short label. */
function Gravita({ cvss, exploited }: { cvss: number | null; exploited: boolean }) {
  return (
    <>
      {cvss != null && (
        <span className={`badge ${cvss >= 9 ? 'badge--error' : cvss >= 7 ? 'badge--warn' : 'badge--muted'}`} style={{ fontSize: 12 }}>
          CVSS {cvss.toFixed(1)}
        </span>
      )}
      {exploited && <strong style={{ color: COLORE_ERRORE, fontSize: 12, marginLeft: 6 }}>{t('sfruttata attivamente (CISA KEV)')}</strong>}
    </>
  )
}

type Libreria = ProgramVulnerabilities['libraries'][number]

/** The drill-down behind a program's badge: its vulnerable libraries, with
 *  the dependency chain that brings each one in and the version that fixes
 *  it, and the open CVEs of the package the program comes from. */
function VulnerabilitaProgramma({ host, app, onChiudi }: { host: FlowHost; app: FlowApp; onChiudi: () => void }) {
  const dati = useAsync<ProgramVulnerabilities | null>(
    () => (host.ci_id ? api.programVulnerabilities(host.ci_id, { applicationId: app.application_id, pkg: app.package })
      : Promise.resolve(null)),
    [host.ci_id, app.key],
  )
  const [aperte, setAperte] = useState<Set<string>>(new Set())
  useEffect(() => {
    // Capture phase: Esc closes this popup only, not the window or the full screen below.
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onChiudi() } }
    window.addEventListener('keydown', esc, true)
    return () => window.removeEventListener('keydown', esc, true)
  }, [onChiudi])
  const apri = (id: string) => setAperte((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const d = dati.data
  const ambito = (c: Libreria) =>
    c.scope === 'direct' ? ` · ${t('diretta')}` : c.scope === 'transitive' ? ` · ${t('transitiva')}` : ''
  const colonneLibrerie: Column<Libreria>[] = [
    {
      key: 'name',
      header: t('Libreria'),
      value: (c) => `${c.name} ${c.version} ${c.ecosystem}${ambito(c)}${c.via.length > 0 ? ` · ${t('portata da')} ${c.via.join(' → ')}` : ''}`,
      render: (c) => {
        const dove = dovesiTrova(c)
        return (
          <>
            <strong>{c.name}</strong> <span className="mono">{c.version}</span>
            <br /><small className="text-muted">
              {c.ecosystem}{ambito(c)}
              {c.via.length > 0 && <> · {t('portata da')} {c.via.join(' → ')}</>}
            </small>
            {dove && (
              <><br /><small className="text-muted">{dove.cosa}: </small>
                <small className="mono" style={{ wordBreak: 'break-all' }}>{dove.file}{dove.dentro ? ` → ${dove.dentro}` : ''}</small>
                {dove.come && <><br /><small className="text-muted">{dove.come}</small></>}</>
            )}
          </>
        )
      },
    },
    {
      key: 'cvss',
      header: t('Gravità'),
      type: 'number',
      align: 'left',
      render: (c) => <Gravita cvss={c.cvss} exploited={c.exploited} />,
    },
    { key: 'fixed', header: t('Corretta in'), mono: true },
    {
      key: 'avvisi',
      header: t('Avvisi'),
      type: 'number',
      align: 'left',
      value: (c) => c.vulnerabilities.length,
      render: (c) => (
        <button className="link" onClick={() => apri(c.id)}>
          {aperte.has(c.id) ? '▾' : '▸'} {t('{n} avvisi', { n: c.vulnerabilities.length })}
        </button>
      ),
    },
  ]
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Vulnerabilità del programma')}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(900px, 96vw)', maxHeight: '90vh' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Vulnerabilità di {programma}', { programma: nomeApp(app) })}</h3>
            <p className="dob-modal__sotto">
              {host.name}{app.process ? <> · <span className="mono">{app.process}</span></> : null}
              {app.path ? <> · <span className="mono">{app.path}</span></> : null}
            </p>
          </div>
          <div className="dob-modal__comandi">
            {host.ci_id && <Link to={`/cis/${host.ci_id}`}>{t('Apri il CI')}</Link>}
            <button className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <div className="dob-modal__corpo">
          {dati.loading && <Spinner />}
          {dati.error != null && <ErrorBanner error={dati.error} />}
          {d && (
            <>
              {d.application && (
                <>
                  <h4 style={{ margin: '4px 0' }}>{t('Librerie vulnerabili ({n} su {totale})', { n: d.libraries.length, totale: d.application.components })}</h4>
                  <p className="field__hint" style={{ marginTop: 0 }}>
                    {t('Le librerie dell’applicazione confrontate con il catalogo OSV. «Portata da» è la catena di dipendenze che la fa entrare: si corregge aggiornando la prima della catena.')}
                  </p>
                  {d.libraries.length === 0 ? <p className="text-muted"><small>{t('Nessuna libreria vulnerabile')}</small></p> : (
                    <DataTable
                      rows={d.libraries}
                      columns={colonneLibrerie}
                      rowKey={(c) => c.id}
                      conteggio={{ nome: t('librerie') }}
                      expand={(c) => (
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {c.vulnerabilities.map((v) => (
                            <li key={v.id} style={{ marginBottom: 4 }}>
                              <span className="mono">{v.id}</span>
                              {v.cves.filter((x) => x !== v.id).length > 0 && <small className="text-muted"> ({v.cves.filter((x) => x !== v.id).join(', ')})</small>}{' '}
                              <Gravita cvss={v.cvss} exploited={v.exploited} />
                              {v.fixed && <small className="text-muted"> · {t('corretta in')} <span className="mono">{v.fixed}</span></small>}
                              {v.summary && <><br /><small>{v.summary}</small></>}
                            </li>
                          ))}
                        </ul>
                      )}
                      expanded={(c) => aperte.has(c.id)}
                    />
                  )}
                </>
              )}
              {d.package && (
                <>
                  <h4 style={{ margin: '14px 0 4px' }}>{t('Il pacchetto {pacchetto} ({n} CVE aperte)', { pacchetto: d.package.name, n: d.package.vulnerabilities.length })}</h4>
                  {d.package.vulnerabilities.length === 0 ? <p className="text-muted"><small>{t('Nessuna CVE aperta sul pacchetto')}</small></p> : (
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      {d.package.vulnerabilities.map((v) => (
                        <li key={v.finding_id} style={{ marginBottom: 6 }}>
                          <span className="mono">{v.id}</span>{' '}
                          <Gravita cvss={v.cvss} exploited={v.exploited} />
                          {v.installed && <small className="text-muted"> · {t('installata')} <span className="mono">{v.installed}</span></small>}
                          {v.summary && <><br /><small>{v.summary}</small></>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {d.package.vulnerabilities.some((v) => v.command) && (
                    <p className="field__hint">{t('Si chiudono aggiornando il pacchetto')}: <span className="mono">{d.package.vulnerabilities.find((v) => v.command)?.command} {d.package.name}</span></p>
                  )}
                </>
              )}
              {!d.application && !d.package && <p className="text-muted">{t('Per questo programma non ci sono librerie né pacchetto noti.')}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
